import { Component, Event, EventEmitter, forceUpdate, h, Host, Listen, Prop, State, Watch } from '@stencil/core';
import clsx from 'clsx';
import { v4 } from 'uuid';

import store from '@/store';

@Component({
  tag: 'fds-checkbox',
  styleUrl: 'styles/checkbox.scss',
  scoped: true,
})
export class Checkbox {
  @State() isChecked = false;

  /**
   * The name attribute of the checkbox
   */
  @Prop() name!: string;

  /**
   * The id attribute of the checkbox, autogenerated if not provided
   */
  @Prop() checkboxId: string = v4();

  /**
   * If true, the checkbox will render default as checked
   */
  @Prop() defaultChecked = false;

  @Watch('defaultChecked')
  defaultCheckedChanged(value: boolean) {
    this.isChecked = value;
  }

  /**
   * If true, the checkbox will default render as disabled
   */
  @Prop() disabled = false;

  /**
   * If true, the checkbox will render as required
   */
  @Prop() required = false;

  /**
   * Display label reversed to the original position
   */
  @Prop() labelReversed = false;

  /**
   * Emitted when the checkbox is checked/unchecked
   */
  @Event() fdsCheckboxChecked: EventEmitter<boolean>;

  @Watch('labelReversed')
  onLabelReversedChange() {
    forceUpdate(this);
  }

  componentWillLoad() {
    this.isChecked = this.defaultChecked;
  }

  @Listen('keydown')
  handleKeyDown(ev: KeyboardEvent) {
    if (ev.code === 'Space' && !this.disabled) {
      ev.preventDefault();
      this.isChecked = !this.isChecked;
      this.fdsCheckboxChecked.emit(this.isChecked);
    }
  }

  private handleClick = () => {
    if (!this.disabled) {
      this.isChecked = !this.isChecked;
      this.fdsCheckboxChecked.emit(this.isChecked);
    }
  };

  render() {
    return (
      <Host class={store.mode ?? ''}>
        <label htmlFor={this.checkboxId} class={clsx('checkbox-container', { disabled: this.disabled })}>
          <input
            tabindex={-1}
            type="checkbox"
            name={this.name}
            id={this.checkboxId}
            checked={this.isChecked}
            required={this.required}
            disabled={this.disabled}
            onClick={this.handleClick}
          />
          <div class={clsx('icon', { 'icon--reversed': this.labelReversed })}>
            <fds-icon
              tabindex={this.disabled ? -1 : 0}
              name={this.isChecked ? 'check_box_filled' : 'check_box_blank'}
            />
            <slot />
            {this.required && <span class="fds-checkbox__required">*</span>}
          </div>
        </label>
      </Host>
    );
  }
}
